import { ShieldCheck, UserRound } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useMyProfile } from '@/features/admin/api'
import { useMe } from '@/features/auth/api'
import { relativeTime } from '@/lib/format'
import { Link } from '@/lib/router'

/**
 * The signed-in user's own details. View only: email is the password-reset
 * channel and mobile the login identity, so changes go through a user admin
 * (Users → Manage → Details), who is audited and whose change is emailed to
 * the user.
 */
export function ProfilePage() {
  const me = useMe().data
  const profile = useMyProfile()
  const p = profile.data

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-navy-900">
          <UserRound className="h-5 w-5" /> Profile
        </h1>
        <p className="text-sm text-muted-foreground">Your account at {me?.tenantName || me?.tenantSlug || '—'}.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your details</CardTitle>
          <CardDescription>To change any of these, ask your administrator.</CardDescription>
        </CardHeader>
        <CardContent>
          {profile.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : profile.isError || !p ? (
            <p className="text-sm text-negative">Couldn't load your details.</p>
          ) : (
            <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <Field label="Name" value={p.fullName} />
              <Field label="Emp ID" value={p.empId} mono />
              <Field label="Email" value={p.email ?? 'Not set'} muted={!p.email} />
              <Field label="Mobile" value={p.mobile ?? 'Not set'} muted={!p.mobile} mono={!!p.mobile} />
              <Field label="Branch" value={me?.branch ? `${me.branch.name} (${me.branch.code})` : '—'} />
              <Field label="Last sign-in" value={p.lastLoginAt ? relativeTime(p.lastLoginAt) : '—'} />
              <div className="sm:col-span-2">
                <dt className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Roles</dt>
                <dd className="mt-1 flex flex-wrap gap-1">
                  {(me?.roles ?? []).length === 0 ? (
                    <span className="text-muted-foreground">None</span>
                  ) : (
                    me!.roles.map((r) => (
                      <Badge key={r} tone="navy">
                        {r}
                      </Badge>
                    ))
                  )}
                </dd>
              </div>
            </dl>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Security
          </CardTitle>
          <CardDescription>
            Change your password and set up two-factor authentication.{' '}
            <Link to="/profile/security" className="text-navy-700 underline">
              Open security settings
            </Link>
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  )
}

function Field({ label, value, mono, muted }: { label: string; value: string; mono?: boolean; muted?: boolean }) {
  return (
    <div>
      <dt className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 ${mono ? 'font-mono' : ''} ${muted ? 'text-muted-foreground' : 'text-navy-900'}`}>
        {value}
      </dd>
    </div>
  )
}
